import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, OutputEmitterRef, Signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { InputComponent, ModalService } from 'mbs-ui-kit';
import { OptionalNetworkSettings, TreeInModalComponent } from 'mbs-ui-shared@components/tree-in-modal/tree-in-modal.component';
import { networkValidPath } from 'mbs-ui-shared@constants';
import { linuxPathValidator, networkPathValidator, winPathValidator } from 'mbs-ui-shared@utils/validators';
import { noop } from 'rxjs';
import { DestinationFolderParams } from 'wizards/models/destination-models';
import { ParamsForTreeData } from 'wizards/services/wizard-steps.service';

@Component({
  selector: 'mbs-destination-folder-input',
  templateUrl: './destination-folder-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, I18NextPipe, ReactiveFormsModule],
  standalone: true
})
export class DestinationFolderInputComponent {
  private modalService = inject(ModalService);
  private i18nPipe = inject(I18NextPipe);

  public readonly control = input.required<FormControl<string>>();
  public readonly destinationFolderParams = input<DestinationFolderParams>(null);
  public readonly requiredValidationEnabled = input<boolean>(true);
  public readonly pathValidationEnabled = input<boolean>(true);
  public readonly disabledSelf = input<boolean>(false);
  public readonly isUnix = input<boolean>(false);
  public readonly hasTargetComputer = input<boolean>(false);
  public readonly name = input<string>('destination-folder');
  public readonly inputId = input<string>('destination-folder-input');
  public readonly buttonId = input<string>('destination-folder-tree-button');
  public readonly label = input<string>('wizards:destinationOld.destFolderLabel');
  public readonly placeholder = input<string>('C:\\Folder Name');
  public readonly browseText = input<string>('Browse');

  public readonly validationChange: OutputEmitterRef<boolean> = output<boolean>();
  public readonly validationErrorsChange: OutputEmitterRef<ValidationErrors | null> = output<ValidationErrors | null>();

  private readonly isUnixPath: Signal<boolean> = computed(
    () => (this.isUnix() && !this.hasTargetComputer()) || !!this.destinationFolderParams()?.dataForPath?.isUnix
  );
  private readonly networkPathAllowed: Signal<boolean> = computed(() => !this.isUnixPath() && !this.destinationFolderParams()?.isOffline);
  private readonly networkShareTreeAllowed: Signal<boolean> = computed(
    () => this.networkPathAllowed() && !!this.destinationFolderParams()?.canUseShareTree
  );

  private readonly destinationValidator: ValidatorFn = (control): ValidationErrors | null => {
    if (this.requiredValidationEnabled()) {
      const isRequired = Validators.required(control);

      if (isRequired) return isRequired;
    }

    if (!this.pathValidationEnabled()) return null;

    if (this.isUnixPath()) return linuxPathValidator(control);

    if (!this.networkPathAllowed()) return winPathValidator(control);

    const winPath = winPathValidator(control);
    const netPath = networkPathValidator(control);

    return !winPath || !netPath ? null : winPath || netPath;
  };

  constructor() {
    effect((onCleanup) => {
      const destinationControl = this.control();
      this.requiredValidationEnabled();
      this.pathValidationEnabled();
      this.isUnixPath();
      this.networkPathAllowed();

      destinationControl.removeValidators(this.destinationValidator);
      if (this.requiredValidationEnabled() || this.pathValidationEnabled()) destinationControl.addValidators(this.destinationValidator);
      destinationControl.updateValueAndValidity();
      this.emitValidationState();

      const subscription = destinationControl.statusChanges.subscribe(() => this.emitValidationState());

      onCleanup(() => {
        subscription.unsubscribe();
        destinationControl.removeValidators(this.destinationValidator);
        destinationControl.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  public destinationFolderTreeOpen(): void {
    if (this.disabledSelf()) return;

    const params = this.destinationFolderParams();
    const folder = this.control().value;
    const dataForTree = {
      title: this.i18nPipe.transform('wizards:destinationOld.dest_tree_in_modal_title', { format: 'title' }),
      hid: params?.hid,
      resultFolders: folder ? [folder] : [],
      isEncrypted: false,
      hideFileType: true,
      oldBackupContentVariant: true,
      onlyOneSelect: true,
      params: this.getNewTreeParams(),
      dataForPath: params?.dataForPath,
      optionalNetworkSettings: this.getOptionalNetworkSettings(folder)
    };

    this.modalService
      .openCustom(TreeInModalComponent, { data: dataForTree, collapsing: true })
      .then((result: string[]) => {
        if (result?.length) this.control().setValue(result[0]);
      })
      .catch(noop);
  }

  public needHide = (item: unknown & { type: number }): boolean => +item.type === 4;

  private getOptionalNetworkSettings(folder: string): OptionalNetworkSettings {
    if (!this.networkShareTreeAllowed()) return null;

    return {
      params: this.getNewNetworkShareTreeParams(),
      isNetwork: !!folder && networkValidPath.test(folder),
      needHide: this.needHide
    };
  }

  private getNewTreeParams(): ParamsForTreeData {
    return {
      agentType: 'Backup',
      commandType: 'getComputerContent',
      params: { path: '', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
    };
  }

  private getNewNetworkShareTreeParams(): ParamsForTreeData {
    return {
      agentType: 'Backup',
      commandType: 'getComputerContent',
      params: { path: '\\\\', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
    };
  }

  private emitValidationState(): void {
    const destinationControl = this.control();

    this.validationChange.emit(destinationControl.valid);
    this.validationErrorsChange.emit(destinationControl.errors);
  }
}
